import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling');
  const [details, setDetails] = useState(null);
  const polled = useRef(false);

  useEffect(() => {
    if (!sessionId || polled.current) return;
    polled.current = true;
    pollStatus(sessionId, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollStatus = async (sid, attempt) => {
    if (attempt >= 6) {
      setStatus('timeout');
      return;
    }
    try {
      const res = await fetch(`${API}/billing/status/${sid}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to check status');
      const data = await res.json();
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setDetails(data);
        return;
      }
      if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      // Continue polling
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4" data-testid="billing-success-page">
      <Card className="max-w-md w-full border-[#E2E8F0] shadow-lg">
        <CardContent className="p-8 text-center">
          {status === 'polling' && (
            <>
              <Loader2 className="w-12 h-12 text-[#1A3E6F] mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-bold text-[#0F172A] font-['Montserrat'] mb-2">Processing payment...</h2>
              <p className="text-sm text-[#64748B]">Please wait while we confirm your payment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-[#4EBC87]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-9 h-9 text-[#4EBC87]" />
              </div>
              <h2 className="text-2xl font-bold text-[#0F172A] font-['Montserrat'] mb-2">Payment Successful!</h2>
              <p className="text-sm text-[#64748B] mb-4">Your subscription is now active.</p>
              {details && (
                <div className="bg-[#F8F9FA] rounded-lg p-4 mb-6 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-[#64748B]">Plan</span>
                    <span className="font-medium text-[#0F172A] capitalize">{details.plan_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748B]">Locations</span>
                    <span className="font-medium text-[#0F172A]" data-testid="billing-success-locations">
                      {details.location_count ?? details.outlet_count ?? '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748B]">Amount</span>
                    <span className="font-medium text-[#0F172A]">${(details.amount_total / 100).toFixed(2)}</span>
                  </div>
                </div>
              )}
              <Button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn"
                className="bg-[#1A3E6F] hover:bg-[#122c50] font-['Montserrat'] font-semibold px-8 py-5">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {(status === 'timeout' || status === 'expired') && (
            <>
              <div className="w-16 h-16 bg-[#D62B27]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-9 h-9 text-[#D62B27]" />
              </div>
              <h2 className="text-xl font-bold text-[#0F172A] font-['Montserrat'] mb-2">
                {status === 'expired' ? 'Session Expired' : 'Status Check Timed Out'}
              </h2>
              <p className="text-sm text-[#64748B] mb-4">
                {status === 'expired' ? 'Your payment session expired. Please try again.' : 'Please check your email for payment confirmation.'}
              </p>
              <Button onClick={() => navigate('/pricing')} variant="outline" className="border-[#1A3E6F] text-[#1A3E6F]">
                Back to Pricing
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
